<template>
<section class="section-container">
<div v-if="shares_disabled" class="container">
    <upgrade-warning-box :feature="'Shares'"></upgrade-warning-box>
</div>
<div v-else class="container-fluid pt-4">
    <!-- Loader -->
    <div v-if="shares === null" class="row">
        <div class="col-3"></div>
        <div class="col-12 col-lg-6">
            <hero-box bg-class="bg-gradient-info" icon="loader d-block" />
        </div>
    </div>

    <template v-else>

        
    <div v-if="active_shares_limit && shares.filter(s => s.active).length >= active_shares_limit" class="alert alert-warning">
        <i class="ion-alert-circled mr-3"/>{{ user.team_settings.name }} reached the limit of active shares. Upgrade your account to enable more shares!
    </div>

    <!-- Filters -->
    <div class="mb-3 d-flex align-items-center gap-2">
        <input type="text" class="form-control" style="max-width: 300px" placeholder="File/folder name or share link" v-model="search.name" />
        <label class="switch switch-brand nowrap d-flex align-items-center gap-1 mb-0">
            <input type="checkbox" checked="checked" v-model="search.hide_disabled" />
            <span class="mr-1"></span>
            Hide Disabled
        </label>
    </div>

    <masonry-wall v-if="shares.length" :items="shares_filtered" :min-columns="1" :max-columns="6" :column-width="340" :gap="32">
        <template #default="{ item }">
            <share-card 
                :share="item" 
                :created_by="get_share_creator_name(item)"
                :show_details="item.show_info"
                :name_search="search.name"
                :max_files_to_show="max_files_to_show"
                @open="open_share_edit(item)"
                @activate="activate_share(item)"
                @revoke="revoke_share(item)"
                @delete="delete_share(item)"
            />
        </template>
    </masonry-wall>
    <!-- No shares hero -->
     <div v-else class="row">
        <div class="col-3"></div>
        <div class="col-12 col-lg-6">
            <hero-box bg-class="bg-gradient-info" icon="ion-qr-scanner" text="Nothing is shared" />
        </div>
     </div>
    </template>
</div>
</section>
</template>

<script>

import { ShareService } from '@/services/share-service.js'
import { Utils } from '@/helpers/utils.js'

import upgradeWarningBox from '@/components/upgrade_warning_box.vue'
import heroBox from '@/components/hero-box.vue'
import { bus, events } from '@/helpers/event_bus'
import pageActivity from '@/mixins/page-activity'
import shareCard from '@/components/share_card.vue'

import { init_shares } from '@/helpers/init-shares'

export default {

    mixins: [pageActivity],

    props: {
        user: Object
    },

    components: {
        upgradeWarningBox,
        heroBox,
        shareCard
    },

    data() {
        return {
            shares: null,
            shares_loading: false,
            shared_entities: null,

            max_files_to_show: 2,

            search: {
                name: "",
                hide_disabled: true
            },

            ShareService: ShareService,
            Utils: Utils,

            edit_share: null
        }
    },

    computed: {
        
        shares_disabled(){
            // Assume shares are enabled until team settings are loaded
            return this.user && this.user.team_settings ? this.user.team_settings.config.share_enabled === false : false
        },

        active_shares_limit(){
            // Max active shares allowed for the team, or `false` if unlimited
            var limit = this.user?.team_settings?.config?.active_shares_limit || 0
            return (!limit || limit == 0) ? false : limit
        },

        shares_filtered(){
            if(!this.shares) return []
            
            let shares = this.shares.filter(s => s)
            if(this.search.name.length){
                const name_filter = this.search.name.toLowerCase()
                shares = shares.filter(s => {
                    // Include the share if any of the following is true:
                    // - The share has any entities (files/folder) with a matching name fragment
                    //    (if any of the matched files is hidden, set show_all_files=true to reveal all matching files)
                    // - The share path includes the searched string
                    // - The share link is exactly the searched string
                    
                    // File/folder name search (find first index the reversed list so we can determine if it's hidden)
                    const largest_matching_entity_idx = s.entities.toReversed().map(e => e.name.toLowerCase()).findIndex(entity_name_lower => entity_name_lower.includes(name_filter))
                    if(largest_matching_entity_idx < s.entities.length - this.max_files_to_show){ // the index is in the reversed list, need to count from the end!
                        // The matching file name is currently hidden, show all
                        s.show_all_files = true
                    }
                    return (largest_matching_entity_idx >= 0) // File name match
                            || (s.path && s.path.toLowerCase().includes(name_filter)) // Path match
                            || s.link == this.search.name // Link match
                })
            }

            if(this.search.hide_disabled){
                // Show only non-disabled
                shares = shares.filter(s => s.active)
            }
            return shares
        },
    },

    watch: {
        // If all search results are all disabled, uncheck "hide disabled"
        shares_filtered(new_shares_filtered){
            if(this.search.hide_disabled && new_shares_filtered.every(s => !s.active)){
                this.search.hide_disabled = false
            }
        }
    },

    mounted(){
        this.load_shares()
        bus.$on(events.SHARE_UPDATED, this.share_updated)
    },

    methods: {

        onPageActivityChange() {
            // Invoked when the Skyflok webapp lost focus or came back
            // (overrides the onPageActivityChange function from)
            if (this.isPageActive) {
                // SkyFlok webapp became active
                // reload the shares
                this.load_shares()
            } 
        },

        async load_shares(){
            
            this.shares_loading = true

            // Error handler for loading shares or shared files
            const load_shares_error = (error_response) => {
                console.error(error_response)
                Utils.show_error("Error loading shares, please try again later!")
                this.shares_loading = false
                return null
            }
            
            const shares = await ShareService.list_shares()
                                .then(res => res.body) 
                                .catch(err => load_shares_error(err))
            if(!shares) return
            
            // Init shares
            if(!await init_shares(shares, this.shares)) load_shares_error(null)

            // If all shares are disabled, show them
            if(!shares.filter(s => s.active)) 
                this.search.hide_disabled = false
            
            // Masonry.js does not support .splice and .push on the source array, so re-assign this.shares
            this.shares = [...shares]
            this.shares_loading = false
        },

        revoke_share(share){
            ShareService.revoke_share(share.id).then(() => {
                share.active = false
                Utils.show_success("Share disabled")
            })
            .catch(err => {
                let str = "Error disabling share"
                if(err.message){ str += ": " + err.message }
                Utils.show_error(str)
            })
        },

        activate_share(share){
            ShareService.activate_revoked_share(share.id).then(() => {
                share.active = true
                Utils.show_success("Share activated")
            })
            .catch(err => {
                let str = "Error activating share"
                if(err.message){ str += ": " + err.message }
                Utils.show_error(str)
            })
        },

        delete_share(share){
            ShareService.delete_share(share.id).then(() => {
                Utils.show_success("Share deleted")
                // Masonry.js does not support .splice and .push, so re-assign the shares array
                this.shares = this.shares.filter(s => s.id !== share.id);
            })
            .catch(err => {
                let str = "Error deleting share"
                if(err.message){ str += ": " + err.message }
                Utils.show_error(str)
            })
        },

        open_share_edit(share){
            bus.$emit(events.EDIT_SHARE, structuredClone(share))
        },

        share_updated(share){
            // Callback after a share has been successfully updated
            const local_share = this.shares.find(s => s.id == share.id)
            if(!local_share){
                console.error(`Cannot find share #${share.id} in local shares list!`, this.shares)
            }
            
            // Instead of replacing the share object, we only update its writable properties
            const writable_fields = ['password_protected', 'download_limit', 'message', 'name', 'expire']
            writable_fields.forEach(f => local_share[f] = share[f]);
            this.shares = [...this.shares]
        },

        get_share_creator_name(share){
            if (!this.user || !this.user.team) return ""
            const share_creator = this.user.team.find(user => user.id == share.created_by)
            if(!share_creator) return "[deleted user]"
            return share_creator.name || share_creator.email_address
        },
    },

}
</script>